import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { QuestionLibraryTabs } from '../../../../pages/DealGPT/QuestionLibrary';
import { CollectionTags } from '../../../../Types/enums';
import { User } from '../../../../Types/user';
import { collectionCreatorsPathUrl } from '../../../../util/routesUrls';
import { useAuth } from '../../../Providers/AuthProvider';

interface FiltersModalProps {
  open: boolean;
  tab: QuestionLibraryTabs;
  selectedUsers: string[];
  selectedTags: string[];
  onClear: () => void;
  onClose: () => void;
  onApply: (selectedUsers: string[], selectedTags: string[]) => void;
}

const QuestionLibraryFilters: React.FC<FiltersModalProps> = ({
  open,
  tab,
  selectedUsers,
  selectedTags,
  onClear,
  onClose,
  onApply,
}) => {
  const { currentUser } = useAuth();
  const { data: users } = useFetchData<User[]>({ urlPath: collectionCreatorsPathUrl() });
  const [usersFilter, setUsersFilter] = useState<string[]>(selectedUsers);
  const [tagsFilter, setTagsFilter] = useState<string[]>(selectedTags);

  const validUsers = useMemo(
    () => uniqBy([...(users ?? []), ...(currentUser ? [currentUser] : [])], 'id'),
    [currentUser, users]
  );

  const handleUserFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUsersFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
  }, []);

  const handleTagsFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTagsFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
  }, []);

  useEffect(() => {
    setUsersFilter(selectedUsers);
  }, [selectedUsers]);

  useEffect(() => {
    setTagsFilter(selectedTags);
  }, [selectedTags]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="subtitle1">Filters</Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            minHeight: '150px',
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            padding: 1,
            gap: 2,
          }}
        >
          <TextField
            value={usersFilter}
            onChange={handleUserFilterChange}
            SelectProps={{ multiple: true }}
            disabled={tab === QuestionLibraryTabs.MY_COLLECTIONS}
            label="Users"
            variant="outlined"
            size="small"
            fullWidth
            select
          >
            {validUsers?.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.preferredName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            value={tagsFilter}
            onChange={handleTagsFilterChange}
            SelectProps={{ multiple: true }}
            label="Tags"
            variant="outlined"
            size="small"
            fullWidth
            select
          >
            {Object.values(CollectionTags).map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClear}
          disabled={usersFilter.length === 0 && tagsFilter.length === 0}
          variant="outlined"
        >
          Clear
        </Button>
        <Button onClick={() => onApply(usersFilter, tagsFilter)} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionLibraryFilters;
